import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useDealContext } from "../hooks/useDealContext";
import EmptyState from "../components/EmptyState";
import LoadingSpinner from "../components/LoadingSpinner";
import DetailViewPanel from "../components/detail-views/DetailViewPanel";
import { DetailViewTrigger } from "../components/detail-views";
import ExpenseDetailPanel from "../components/detail-views/ExpenseDetailPanel";
import type { ExpenseLineItem } from "../components/detail-views/ExpenseDetailPanel";
import CapitalDetailPanel from "../components/detail-views/CapitalDetailPanel";
import type { TimeSeriesEntry } from "../components/detail-views/types";
import { DollarSign, Upload, ChevronRight, ChevronDown, Download, Sliders, X, Pencil, Check, Trash2 } from "lucide-react";
import {
  fetchDocuments, fetchScenarios, fetchDeal, updateExpenseOverrides,
  fetchExpenseDetail, saveExpenseDetail, deleteExpenseLineItem,
  type DocumentMeta, type ProjectionResult, type TenantPGIDetail, type ReimbursementDetail, type ScenarioResponse,
  type TaxAssessment,
} from "../api/client";
import { fmtCurrencyAcct, fmtPSF, fmtPercent } from "../utils/format";
import { downloadCSV } from "../utils/csv";
import { useToast } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import HistoricalTaxTab from "../components/tax/HistoricalTaxTab";
import PostSaleTaxTab from "../components/tax/PostSaleTaxTab";

interface ExpenseItem {
  label: string;
  amount: number;
  is_subtotal: boolean;
  category: string | null;
  is_non_operating?: boolean;
}

interface ExpenseGroupData {
  subtotal: number;
  items: { name: string; amount: number }[];
}

interface YearData {
  year: number | null;
  period_label: string;
  revenue_items: ExpenseItem[];
  expense_items: ExpenseItem[];
  total_revenue: number;
  total_expenses: number;
  noi: number;
  real_estate_taxes: number;
  insurance: number;
  utilities: number;
  repairs_and_maintenance: number;
  janitorial: number;
  landscaping: number;
  management_fee: number;
  administrative: number;
  capital_expenditures: number;
  reserves: number;
  base_rent: number;
  recovery_income: number;
  other_income: number;
  adjustments: number;
  expense_groups?: Record<string, ExpenseGroupData>;
  is_partial_year?: boolean;
  period_months?: number | null;
}

interface OpexData {
  property_name: string | null;
  years: YearData[];
  flags: string[];
  confidence_score?: number;
}

const EXPENSE_GROUPS = [
  {
    key: "cam",
    label: "CAM",
    children: [
      { key: "utilities", label: "Utilities" },
      { key: "repairs_and_maintenance", label: "Repairs & Maintenance" },
      { key: "janitorial", label: "Janitorial / Cleaning" },
      { key: "landscaping", label: "Landscaping / Grounds" },
      { key: "security", label: "Security Services" },
      { key: "administrative", label: "Administrative / G&A" },
    ],
  },
  { key: "insurance", label: "Insurance", children: [{ key: "insurance", label: "Insurance" }] },
  { key: "taxes", label: "Real Estate Taxes", children: [{ key: "real_estate_taxes", label: "Real Estate Taxes" }] },
  { key: "management_fee", label: "Management Fee", children: [{ key: "management_fee", label: "Management Fee" }] },
] as const;

const BELOW_LINE_CATEGORIES = [
  { key: "capital_expenditures", label: "Capital Expenditures" },
  { key: "reserves", label: "Reserves" },
] as const;

/** Compute a group's subtotal from parsed data. */
function getGroupSubtotal(y: YearData, group: typeof EXPENSE_GROUPS[number]): number {
  if (y.expense_groups?.[group.key]) {
    return y.expense_groups[group.key].subtotal;
  }
  return (group.children as readonly { key: string; label: string }[]).reduce(
    (sum: number, c) => sum + ((y[c.key as keyof YearData] as number) || 0),
    0,
  );
}

/** Compute total operating expenses from the four displayed groups (excludes non-operating items). */
function getComputedTotalExpenses(y: YearData): number {
  return EXPENSE_GROUPS.reduce((sum, group) => sum + getGroupSubtotal(y, group), 0);
}

export default function OperatingStatementPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<"os" | "historical_tax" | "post_sale_tax">("os");
  const [opexData, setOpexData] = useState<OpexData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRevenue, setExpandedRevenue] = useState<Set<string>>(new Set());
  const [projectionData, setProjectionData] = useState<ProjectionResult | null>(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrides, setOverrides] = useState<Array<{ category: string; action: string; value?: number; line_item?: string; note: string }>>(
    () => (deal.property_data?.expense_overrides || []).map((o: any) => ({ ...o, note: o.note || "" }))
  );
  const [editingOverride, setEditingOverride] = useState<string | null>(null);
  const [overrideForm, setOverrideForm] = useState({ action: "replace", value: "", note: "" });

  // Inline cell editing state: "groupKey:itemName:yearIndex"
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editingCellValue, setEditingCellValue] = useState("");

  // PSF toggle
  const [showPSF, setShowPSF] = useState(false);

  // Expense detail panel state
  const [expDetailOpen, setExpDetailOpen] = useState(false);
  const [expDetailCategory, setExpDetailCategory] = useState("");
  const [expDetailCategoryName, setExpDetailCategoryName] = useState("");
  const [expDetailEntries, setExpDetailEntries] = useState<TimeSeriesEntry[]>([]);
  const [expDetailDirty, setExpDetailDirty] = useState(false);
  const [expDetailSaving, setExpDetailSaving] = useState(false);
  const [expDetailSchedule, setExpDetailSchedule] = useState<any[]>([]);
  const [expDetailLineItems, setExpDetailLineItems] = useState<ExpenseLineItem[]>([]);

  // Capital detail panel state
  const [capDetailOpen, setCapDetailOpen] = useState(false);
  const [capDetailEntries, setCapDetailEntries] = useState<TimeSeriesEntry[]>([]);
  const [capDetailDirty, setCapDetailDirty] = useState(false);
  const [capDetailSaving, setCapDetailSaving] = useState(false);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const toggleRevenue = (key: string) => {
    setExpandedRevenue((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const [hasDocumentButNoParse, setHasDocumentButNoParse] = useState(false);

  /** Fix year labels for display. Handles single-period documents where
   * the parser didn't extract a year, and deduplicates years that appear
   * identically (e.g., two columns both labeled "2024"). */
  const fixYearLabels = (data: OpexData, docFilename?: string): OpexData => {
    if (!data.years || data.years.length === 0) return data;

    const priorYear = new Date().getFullYear() - 1;
    const fixedYears = data.years.map((y, i) => {
      if (y.year) return y;

      // Try to extract year from period_label
      const match = (y.period_label || "").match(/20[12]\d/);
      if (match) return { ...y, year: parseInt(match[0]) };

      // Try to extract year from document filename
      if (docFilename) {
        const fMatch = docFilename.match(/20[12]\d/);
        if (fMatch) return { ...y, year: parseInt(fMatch[0]) };
      }

      // Default to prior year offset by index
      return { ...y, year: priorYear - (data.years.length - 1 - i) };
    });

    // Deduplicate: if two adjacent years have the same label, offset one
    for (let i = 1; i < fixedYears.length; i++) {
      if (fixedYears[i].year === fixedYears[i - 1].year && fixedYears[i].year) {
        // Assume years are sequential — adjust based on position
        fixedYears[i] = { ...fixedYears[i], year: (fixedYears[i - 1].year || priorYear) + 1 };
      }
    }

    return { ...data, years: fixedYears };
  };

  const loadData = useCallback(async () => {
    if (!dealId) return;
    try {
      const docs = await fetchDocuments(dealId);
      const osDoc = docs.find(
        (d: DocumentMeta) =>
          d.file_type === "operating_statement" &&
          d.parse_status === "complete" &&
          d.parse_result
      );
      if (osDoc?.parse_result) {
        const rawData = osDoc.parse_result as unknown as OpexData;
        setOpexData(fixYearLabels(rawData, osDoc.filename));
      } else {
        const anyOsDoc = docs.find(
          (d: DocumentMeta) => d.file_type === "operating_statement"
        );
        if (anyOsDoc) {
          setHasDocumentButNoParse(true);
        }
      }
      // Load projection data for per-tenant drill-down
      try {
        const scenarios = await fetchScenarios(dealId);
        const withProjection = scenarios.find((s: ScenarioResponse) => s.projection_result);
        if (withProjection?.projection_result) {
          setProjectionData(withProjection.projection_result);
        }
      } catch { /* projection data is supplementary — don't block page */ }
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build lookup maps from projection data, keyed by calendar year
  const pgiByYear: Record<number, TenantPGIDetail[]> = {};
  const reimbByYear: Record<number, ReimbursementDetail[]> = {};
  if (projectionData) {
    projectionData.annual_cash_flows?.forEach((acf) => {
      if (acf.tenant_pgi_details) pgiByYear[acf.calendar_year] = acf.tenant_pgi_details;
    });
    projectionData.reimbursement_details?.forEach((rd) => {
      if (!reimbByYear[rd.year]) reimbByYear[rd.year] = [];
      reimbByYear[rd.year].push(rd);
    });
  }

  if (loading) return <LoadingSpinner />;

  const hasOpexData = opexData && opexData.years && opexData.years.length > 0;
  const years = hasOpexData ? opexData.years : [];

  // Tax tab helper: reload deal data after applying tax
  const handleTaxApplied = async () => {
    if (!dealId) return;
    try {
      const fresh = await fetchDeal(dealId);
      setDeal(fresh);
    } catch { /* non-critical */ }
  };

  // Get existing assessments and deal purchase price for tax tabs
  const existingAssessments = (deal.property_data?.tax_assessments || []) as unknown as TaxAssessment[];
  const dealPurchasePrice = deal.property_data?.purchase_price as number | undefined;
  const totalLeasableArea = deal.property_data?.total_leasable_area as number | undefined;

  /** Detect partial years: only use the backend flag.
   * Expense-only documents (no revenue) are budgets, not partial years. */
  const isPartialYear = (y: YearData): boolean =>
    !!y.is_partial_year;

  async function saveOverride(category: string) {
    if (!dealId) return;
    const newOv = {
      category,
      action: overrideForm.action,
      value: overrideForm.action === "replace" ? parseFloat(overrideForm.value) || 0 : undefined,
      note: overrideForm.note,
    };
    const updated = [...overrides.filter(o => o.category !== category), newOv];
    try {
      await updateExpenseOverrides(dealId, updated);
      setOverrides(updated);
      setEditingOverride(null);
      const fresh = await fetchDeal(dealId);
      setDeal(fresh);
      toast.success("Override saved");
    } catch (e: any) {
      toast.error(friendlyError(e?.response?.data?.detail || "Couldn't save the override. Try again."));
    }
  }

  async function clearOverrides() {
    if (!dealId) return;
    try {
      await updateExpenseOverrides(dealId, []);
      setOverrides([]);
      const fresh = await fetchDeal(dealId);
      setDeal(fresh);
      toast.success("Overrides cleared");
    } catch {
      toast.error("Failed to clear overrides");
    }
  }

  async function handleDeleteLineItem(groupKey: string, itemName: string) {
    if (!dealId) return;
    try {
      await deleteExpenseLineItem(dealId, groupKey, itemName);
      toast.success(`Removed "${itemName}"`);
      loadData();
    } catch {
      toast.error("Failed to delete line item");
    }
  }

  async function commitCellEdit(groupKey: string, lineItem: string) {
    if (!dealId) return;
    const newVal = parseFloat(editingCellValue);
    if (isNaN(newVal)) { setEditingCell(null); return; }
    const ovKey = `${groupKey}__${lineItem}`;
    const newOv = { category: ovKey, action: "replace" as const, value: newVal, line_item: lineItem, note: "Inline edit" };
    const updated = [...overrides.filter(o => o.category !== ovKey), newOv];
    try {
      await updateExpenseOverrides(dealId, updated);
      setOverrides(updated);
      setEditingCell(null);
      const fresh = await fetchDeal(dealId);
      setDeal(fresh);
    } catch {
      toast.error("Failed to save edit");
    }
  }

  function exportOpexCSV() {
    const headers = ["Category", ...years.map(y => String(y.year || y.period_label || ""))];
    const rows: (string | number)[][] = [];
    rows.push(["Base Rent", ...years.map(y => y.base_rent || 0)]);
    rows.push(["Recovery Income", ...years.map(y => y.recovery_income || 0)]);
    rows.push(["Other Income", ...years.map(y => y.other_income || 0)]);
    rows.push(["Gross Revenue", ...years.map(y => {
      const computed = (y.base_rent || 0) + (y.recovery_income || 0) + (y.other_income || 0) + (y.adjustments || 0);
      return computed !== 0 ? computed : y.total_revenue || 0;
    })]);
    rows.push([]);
    for (const g of EXPENSE_GROUPS) {
      rows.push([g.label, ...years.map(y => getGroupSubtotal(y, g))]);
    }
    rows.push(["Total Operating Expenses", ...years.map(y => y.total_expenses ?? getComputedTotalExpenses(y))]);
    rows.push(["NOI", ...years.map(y => y.noi != null ? Math.round(y.noi) : Math.round((y.total_revenue || 0) - (y.total_expenses ?? getComputedTotalExpenses(y))))]);
    downloadCSV([headers, ...rows], `${deal.name || "opex"}_operating_statement.csv`);
  }

  // --- Expense detail handlers ---

  async function openExpenseDetail(categoryKey: string, _inputType: string) {
    const labelMap: Record<string, string> = {};
    for (const g of EXPENSE_GROUPS) {
      labelMap[g.key] = g.label;
      for (const c of g.children) labelMap[c.key] = c.label;
    }
    setExpDetailCategory(categoryKey);
    setExpDetailCategoryName(labelMap[categoryKey] || categoryKey);
    setExpDetailOpen(true);

    // Extract line items from the most recent year's expense_groups for this category
    const items: ExpenseLineItem[] = [];
    if (opexData && opexData.years.length > 0) {
      const mostRecentYear = opexData.years[opexData.years.length - 1];
      const groupData = mostRecentYear.expense_groups?.[categoryKey];
      if (groupData?.items) {
        for (const item of groupData.items) {
          if (item.name && item.amount) {
            items.push({ name: item.name, amount: item.amount });
          }
        }
      }
    }
    setExpDetailLineItems(items);

    // Build pre-populated entries from parsed OS data (one per year with group subtotal)
    const buildParsedEntries = (): TimeSeriesEntry[] => {
      if (!opexData || opexData.years.length === 0) return [];
      const group = EXPENSE_GROUPS.find(g => g.key === categoryKey);
      if (!group) return [];
      return opexData.years
        .filter(y => y.year)
        .map(y => ({
          entry_id: crypto.randomUUID(),
          start_date: `${y.year}-01-01`,
          duration_years: 0,
          duration_months: 0,
          duration_days: 0,
          entry_type: "expense",
          units: "$/Year",
          amount: getGroupSubtotal(y, group),
          source: "parsed" as const,
          extra: { growth_rate: 0.03, override: false },
        }));
    };

    try {
      const data = await fetchExpenseDetail(dealId!, categoryKey);
      const entries = (data.entries as TimeSeriesEntry[]) || [];
      // Pre-populate from parsed data if API returns empty/all-zero entries
      if (entries.length === 0 || entries.every(e => !e.amount)) {
        const parsed = buildParsedEntries();
        setExpDetailEntries(parsed.length > 0 ? parsed : entries);
      } else {
        setExpDetailEntries(entries);
      }
      setExpDetailSchedule((data.projected_schedule as any[]) || []);
      setExpDetailDirty(false);
    } catch {
      setExpDetailEntries(buildParsedEntries());
      setExpDetailSchedule([]);
    }
  }

  async function saveExpDetail() {
    if (!dealId) return;
    setExpDetailSaving(true);
    try {
      const result = await saveExpenseDetail(
        dealId,
        expDetailCategory,
        expDetailEntries as unknown as Record<string, unknown>[],
      );
      setExpDetailEntries((result.entries as TimeSeriesEntry[]) || []);
      setExpDetailSchedule((result.projected_schedule as any[]) || []);
      setExpDetailDirty(false);

      // Update local opexData so OS page totals reflect saved amounts
      if (opexData) {
        const group = EXPENSE_GROUPS.find(g => g.key === expDetailCategory);
        if (group) {
          const amountByYear = new Map<number, number>();
          for (const entry of expDetailEntries) {
            const sd = entry.start_date || "";
            const yrMatch = sd.match(/^(\d{4})/);
            if (yrMatch && entry.amount != null) {
              amountByYear.set(parseInt(yrMatch[1], 10), entry.amount as number);
            }
          }
          if (amountByYear.size > 0) {
            const updatedYears = opexData.years.map(y => {
              const newAmount = amountByYear.get(y.year ?? 0);
              if (newAmount == null) return y;
              const oldTotal = getGroupSubtotal(y, group);
              if (oldTotal === 0 || group.children.length <= 1) {
                // Single-field category or no existing breakdown: set the field directly
                const fieldKey = group.children[0].key as keyof YearData;
                return { ...y, [fieldKey]: Math.round(newAmount) };
              }
              // Multi-field group: scale each child proportionally
              const ratio = newAmount / oldTotal;
              const updated = { ...y };
              for (const child of group.children) {
                const k = child.key as keyof YearData;
                const oldVal = (y[k] as number) || 0;
                (updated as Record<string, unknown>)[k] = Math.round(oldVal * ratio);
              }
              return updated as YearData;
            });
            setOpexData({ ...opexData, years: updatedYears });
          }
        }
      }

      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setExpDetailSaving(false);
    }
  }

  function resetExpDetail() {
    setExpDetailEntries([]);
    setExpDetailDirty(false);
  }

  function handleExpEntryChange(entryId: string, key: string, value: unknown) {
    setExpDetailEntries(prev =>
      prev.map(e => {
        if (e.entry_id !== entryId) return e;
        // Year field: store as YYYY-01-01 string for start_date
        if (key === "start_date") {
          const yr = typeof value === "number" ? value : parseInt(String(value), 10);
          return { ...e, start_date: isNaN(yr) ? e.start_date : `${yr}-01-01`, source: "manual" as const };
        }
        // Route growth_rate and override into the extra bag
        if (key === "growth_rate" || key === "override") {
          return { ...e, extra: { ...(e.extra || {}), [key]: value as number | string | boolean | null }, source: "manual" as const };
        }
        return { ...e, [key]: value, source: "manual" as const };
      }),
    );
    setExpDetailDirty(true);
  }

  function handleExpEntryAdd(template: Record<string, unknown>) {
    const newEntry: TimeSeriesEntry = {
      entry_id: crypto.randomUUID(),
      start_date: (template.start_date as string) || new Date().toISOString().slice(0, 10),
      duration_years: 0,
      duration_months: 0,
      duration_days: 0,
      entry_type: "expense",
      units: (template.units as string) || "$/Year",
      amount: (template.amount as number) ?? null,
      source: "manual",
      extra: { growth_rate: (template.growth_rate as number) ?? 0.03, override: false },
    };
    setExpDetailEntries(prev => [...prev, newEntry]);
    setExpDetailDirty(true);
  }

  function handleExpEntryDelete(entryId: string) {
    setExpDetailEntries(prev => prev.filter(e => e.entry_id !== entryId));
    setExpDetailDirty(true);
  }

  // --- Capital detail handlers ---

  async function openCapitalDetail(_categoryKey: string, _inputType: string) {
    setCapDetailOpen(true);
    try {
      const data = await fetchExpenseDetail(dealId!, "capital_expenditures");
      setCapDetailEntries((data.entries as TimeSeriesEntry[]) || []);
      setCapDetailDirty(false);
    } catch {
      setCapDetailEntries([]);
    }
  }

  async function saveCapDetail() {
    if (!dealId) return;
    setCapDetailSaving(true);
    try {
      const result = await saveExpenseDetail(
        dealId,
        "capital_expenditures",
        capDetailEntries as unknown as Record<string, unknown>[],
      );
      setCapDetailEntries((result.entries as TimeSeriesEntry[]) || []);
      setCapDetailDirty(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setCapDetailSaving(false);
    }
  }

  function resetCapDetail() {
    setCapDetailEntries([]);
    setCapDetailDirty(false);
  }

  function handleCapEntryChange(entryId: string, key: string, value: unknown) {
    setCapDetailEntries(prev =>
      prev.map(e =>
        e.entry_id === entryId ? { ...e, [key]: value, source: "manual" as const } : e,
      ),
    );
    setCapDetailDirty(true);
  }

  function handleCapEntryAdd(template: Record<string, unknown>) {
    const newEntry: TimeSeriesEntry = {
      entry_id: crypto.randomUUID(),
      start_date: (template.start_date as string) || new Date().toISOString().slice(0, 10),
      duration_years: 0,
      duration_months: 0,
      duration_days: 0,
      entry_type: "capital",
      units: (template.units as string) || "$ Total",
      amount: (template.amount as number) ?? null,
      source: "manual",
      extra: {
        description: (template.description as string) || "",
        recurrence: (template.recurrence as string) || "One-Time",
        inflation_adj: (template.inflation_adj as boolean) ?? false,
      },
    };
    setCapDetailEntries(prev => [...prev, newEntry]);
    setCapDetailDirty(true);
  }

  function handleCapEntryDelete(entryId: string) {
    setCapDetailEntries(prev => prev.filter(e => e.entry_id !== entryId));
    setCapDetailDirty(true);
  }

  /** Render per-tenant base rent drill-down rows. */
  function renderBaseRentDetail() {
    // Collect unique tenant names and their data keyed by calendar year
    const tenantMap: Record<string, Record<number, TenantPGIDetail>> = {};
    const tenantOrder: string[] = [];
    years.forEach(y => {
      if (!y.year) return;
      const list = pgiByYear[y.year] || [];
      list.forEach(t => {
        if (!tenantMap[t.tenant_name]) {
          tenantMap[t.tenant_name] = {};
          tenantOrder.push(t.tenant_name);
        }
        tenantMap[t.tenant_name][y.year!] = t;
      });
    });

    if (tenantOrder.length > 0) {
      // Sort by first year's contribution descending
      const sorted = Array.from(new Set(tenantOrder)).sort((a, b) => {
        const aVal = Object.values(tenantMap[a])[0]?.annual_contribution ?? 0;
        const bVal = Object.values(tenantMap[b])[0]?.annual_contribution ?? 0;
        return bVal - aVal;
      });
      return sorted.map(name => (
        <tr key={name} className="bg-grove-50/50">
          <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
            {name}
          </td>
          {years.map((y, i) => {
            const detail = y.year ? tenantMap[name]?.[y.year] : undefined;
            return (
              <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
                {detail ? (
                  <div>
                    <div>{fmtVal(detail.annual_contribution)}</div>
                    <div className="text-[9px] text-bark-300">{fmtPSF(detail.base_rent_psf)}</div>
                  </div>
                ) : "\u2014"}
              </td>
            );
          })}
        </tr>
      ));
    }

    // Fallback: show parsed revenue_items with category "base_rent"
    const labels = new Set<string>();
    years.forEach(y => y.revenue_items?.filter(r => r.category === "base_rent" && !r.is_subtotal).forEach(r => labels.add(r.label)));
    if (labels.size === 0 || labels.size === 1) {
      // Single item identical to parent — no additional info to show
      return (
        <tr className="bg-grove-50/50">
          <td colSpan={years.length + 1} className="pl-8 py-2 text-[11px] text-bark-300 italic">
            Per-tenant detail available after running a projection from the Pro Forma tab
          </td>
        </tr>
      );
    }
    return Array.from(labels).map(label => (
      <tr key={label} className="bg-grove-50/50">
        <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
          {label}
        </td>
        {years.map((y, i) => {
          const item = y.revenue_items?.find(r => r.label === label && r.category === "base_rent" && !r.is_subtotal);
          return (
            <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
              {fmtVal(item?.amount ?? 0)}
            </td>
          );
        })}
      </tr>
    ));
  }

  /** Render per-tenant recovery income drill-down rows. */
  function renderRecoveryDetail() {
    const tenantMap: Record<string, Record<number, ReimbursementDetail>> = {};
    const tenantOrder: string[] = [];
    years.forEach(y => {
      if (!y.year) return;
      const list = reimbByYear[y.year] || [];
      list.forEach(t => {
        if (!tenantMap[t.tenant_name]) {
          tenantMap[t.tenant_name] = {};
          tenantOrder.push(t.tenant_name);
        }
        tenantMap[t.tenant_name][y.year!] = t;
      });
    });

    if (tenantOrder.length > 0) {
      const sorted = Array.from(new Set(tenantOrder)).sort((a, b) => {
        const aVal = Object.values(tenantMap[a])[0]?.total_reimbursement ?? 0;
        const bVal = Object.values(tenantMap[b])[0]?.total_reimbursement ?? 0;
        return bVal - aVal;
      });
      return sorted.map(name => (
        <tr key={name} className="bg-grove-50/50">
          <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
            {name}
          </td>
          {years.map((y, i) => {
            const detail = y.year ? tenantMap[name]?.[y.year] : undefined;
            return (
              <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
                {detail ? fmtVal(detail.total_reimbursement) : "\u2014"}
              </td>
            );
          })}
        </tr>
      ));
    }

    // Fallback: parsed revenue_items with category "recovery_income"
    const labels = new Set<string>();
    years.forEach(y => y.revenue_items?.filter(r => r.category === "recovery_income" && !r.is_subtotal).forEach(r => labels.add(r.label)));
    if (labels.size === 0) {
      return (
        <tr className="bg-grove-50/50">
          <td colSpan={years.length + 1} className="pl-8 py-2 text-[11px] text-bark-300 italic">
            No per-tenant detail available &mdash; run a projection from the Pro Forma tab
          </td>
        </tr>
      );
    }
    return Array.from(labels).map(label => (
      <tr key={label} className="bg-grove-50/50">
        <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
          {label}
        </td>
        {years.map((y, i) => {
          const item = y.revenue_items?.find(r => r.label === label && r.category === "recovery_income" && !r.is_subtotal);
          return (
            <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
              {fmtVal(item?.amount ?? 0)}
            </td>
          );
        })}
      </tr>
    ));
  }

  /** Render other income detail rows (non-tenant sources). */
  function renderOtherIncomeDetail() {
    // Use parsed revenue_items with category "other_income"
    const labels = new Set<string>();
    years.forEach(y => y.revenue_items?.filter(r => r.category === "other_income" && !r.is_subtotal).forEach(r => labels.add(r.label)));

    // Fallback: deal's other_income_lines
    if (labels.size === 0) {
      const oiLines: Array<{ source: string; annual_amount: number }> = deal.property_data?.other_income_lines || [];
      if (oiLines.length === 0) {
        return (
          <tr className="bg-grove-50/50">
            <td colSpan={years.length + 1} className="pl-8 py-2 text-[11px] text-bark-300 italic">
              No line item detail available
            </td>
          </tr>
        );
      }
      return oiLines.map(line => (
        <tr key={line.source} className="bg-grove-50/50">
          <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
            {line.source}
          </td>
          {years.map((_, i) => (
            <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
              {fmtVal(line.annual_amount)}
            </td>
          ))}
        </tr>
      ));
    }

    return Array.from(labels).map(label => (
      <tr key={label} className="bg-grove-50/50">
        <td className="pl-8 pr-5 py-1.5 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
          {label}
        </td>
        {years.map((y, i) => {
          const item = y.revenue_items?.find(r => r.label === label && r.category === "other_income" && !r.is_subtotal);
          return (
            <td key={i} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
              {fmtVal(item?.amount ?? 0)}
            </td>
          );
        })}
      </tr>
    ));
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Operating Statement"}</h1>
        <p>{deal.address || ""} &middot; Operating Statement</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-bark-200 print:hidden">
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "os" ? "border-b-2 border-forest-600 text-forest-700" : "text-bark-500 hover:text-bark-700"}`}
          onClick={() => setActiveTab("os")}
        >
          Operating Statement
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "historical_tax" ? "border-b-2 border-forest-600 text-forest-700" : "text-bark-500 hover:text-bark-700"}`}
          onClick={() => setActiveTab("historical_tax")}
        >
          Insert Historical Taxes
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "post_sale_tax" ? "border-b-2 border-forest-600 text-forest-700" : "text-bark-500 hover:text-bark-700"}`}
          onClick={() => setActiveTab("post_sale_tax")}
        >
          Post-Sale Tax Assumptions
        </button>
      </div>

      {/* Tax tabs */}
      {activeTab === "historical_tax" && dealId && (
        <HistoricalTaxTab
          dealId={dealId}
          propertyAddress={deal.address || undefined}
          existingAssessments={existingAssessments}
          onTaxApplied={handleTaxApplied}
        />
      )}
      {activeTab === "post_sale_tax" && dealId && (
        <PostSaleTaxTab
          dealId={dealId}
          purchasePrice={dealPurchasePrice}
          totalLeasableArea={totalLeasableArea}
          onTaxApplied={handleTaxApplied}
        />
      )}

      {/* Operating Statement tab content */}
      {activeTab === "os" && !hasOpexData && hasDocumentButNoParse && (
        <EmptyState
          icon={DollarSign}
          title="Parse error"
          description="Operating statement was uploaded but could not be parsed. Try re-uploading or enhancing with AI."
          action={
            <Link
              to={`/deals/${dealId}/documents`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              Go to Documents
            </Link>
          }
        />
      )}
      {activeTab === "os" && !hasOpexData && !hasDocumentButNoParse && (
        <EmptyState
          icon={DollarSign}
          title="No operating statement data"
          description="Upload and parse an operating statement (P&L, income statement, or NOI report) to see expense detail. Supported formats include Yardi, QuickBooks, and property management summary exports."
          action={
            <Link
              to={`/deals/${dealId}/documents`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              Upload Operating Statement
            </Link>
          }
        />
      )}

      {activeTab === "os" && hasOpexData && <>
      {/* Override summary banner */}
      {overrides.length > 0 && (
        <div className="bg-honey-50 border border-honey-200 rounded-xl px-5 py-3 flex items-center justify-between print:hidden">
          <span className="text-sm text-honey-800">{overrides.length} expense adjustment{overrides.length > 1 ? "s" : ""} applied</span>
          <button onClick={clearOverrides} className="text-xs text-honey-600 hover:text-honey-800 font-medium transition-colors">Clear All</button>
        </div>
      )}

      {/* Data quality warnings */}
      {(() => {
        const warnings = (deal.property_data?.data_quality_warnings ?? []) as Array<{ category: string; severity: string; message: string }>;
        if (warnings.length === 0) return null;
        return (
          <div className="space-y-2 print:hidden">
            {warnings.map((w, i) => (
              <div key={i} className={`rounded-xl px-5 py-3 flex items-start gap-2 ${
                w.severity === "high" ? "bg-red-50 border border-red-200" : "bg-honey-50 border border-honey-200"
              }`}>
                <span className={`text-sm ${w.severity === "high" ? "text-red-700" : "text-honey-800"}`}>
                  {w.message}
                </span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">Operating Statement</h2>
          <p className="text-sm text-bark-400 mt-1">
            {deal.name || "Property"} &mdash;{" "}
            {years.length} {years.length === 1 ? "period" : "periods"}
          {years[0]?.year && years.length > 1
            ? ` (${years[0].year}\u2013${years[years.length - 1].year})`
            : years[0]?.year
            ? ` (${years[0].year})`
            : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          {totalLeasableArea && totalLeasableArea > 0 && (
            <button
              onClick={() => setShowPSF(!showPSF)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                showPSF ? "bg-forest-700 text-white" : "text-bark-500 bg-white border border-grove-300 hover:bg-grove-50"
              }`}
            >
              {showPSF ? "Hide $/SF" : "Show $/SF"}
            </button>
          )}
          <button onClick={exportOpexCSV} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-all">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Revenue Section */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <table className="w-full data-table text-[13px]">
          <thead>
            <tr>
              <th className="text-left min-w-[200px] sticky left-0 bg-white z-10">Revenue</th>
              {years.map((y, i) => (
                <th key={i} className={`num min-w-[130px] ${isPartialYear(y) ? "text-bark-300" : ""}`}>
                  {y.year || y.period_label || `Period ${i + 1}`}
                  {isPartialYear(y) && (
                    <div className="text-[9px] text-honey-600 font-normal">
                      PARTIAL ({y.period_months ? `${y.period_months} mo` : 'YTD'})
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Base Rent — collapsible with per-tenant drill-down */}
            <tr
              className="cursor-pointer hover:bg-grove-50/30 transition-colors"
              onClick={() => toggleRevenue("base_rent")}
            >
              <td className="text-bark-600 sticky left-0 bg-white">
                <span className="flex items-center gap-1">
                  {expandedRevenue.has("base_rent")
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />}
                  Base Rent
                </span>
              </td>
              {years.map((y, i) => (
                <td key={i} className="num">{fmtVal(y.base_rent)}</td>
              ))}
            </tr>
            {expandedRevenue.has("base_rent") && renderBaseRentDetail()}

            {/* Recovery Income — collapsible with per-tenant drill-down */}
            <tr
              className="cursor-pointer hover:bg-grove-50/30 transition-colors"
              onClick={() => toggleRevenue("recovery_income")}
            >
              <td className="text-bark-600 sticky left-0 bg-white">
                <span className="flex items-center gap-1">
                  {expandedRevenue.has("recovery_income")
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />}
                  Expense Recovery Income
                </span>
              </td>
              {years.map((y, i) => (
                <td key={i} className="num">{fmtVal(y.recovery_income)}</td>
              ))}
            </tr>
            {expandedRevenue.has("recovery_income") && renderRecoveryDetail()}

            {/* Other Income — collapsible with source detail */}
            <tr
              className="cursor-pointer hover:bg-grove-50/30 transition-colors"
              onClick={() => toggleRevenue("other_income")}
            >
              <td className="text-bark-600 sticky left-0 bg-white">
                <span className="flex items-center gap-1">
                  {expandedRevenue.has("other_income")
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />}
                  Other Income
                </span>
              </td>
              {years.map((y, i) => (
                <td key={i} className="num">{fmtVal(y.other_income)}</td>
              ))}
            </tr>
            {expandedRevenue.has("other_income") && renderOtherIncomeDetail()}

            {/* Adjustments (concessions, prior-year adjustments, etc.) */}
            {years.some(y => (y.adjustments || 0) !== 0) && (
              <tr>
                <td className="text-bark-600 sticky left-0 bg-white pl-4">Adjustments / Concessions</td>
                {years.map((y, i) => (
                  <td key={i} className="num text-red-600">{fmtVal(y.adjustments)}</td>
                ))}
              </tr>
            )}

            {/* Unclassified Revenue — gap between total_revenue and sum of categories.
                Shows only when the parser's total_revenue exceeds categorized items. */}
            {years.some(y => {
              const categorized = (y.base_rent || 0) + (y.recovery_income || 0) + (y.other_income || 0) + (y.adjustments || 0);
              return y.total_revenue > 0 && categorized > 0 && (y.total_revenue - categorized) > 100;
            }) && (
              <tr>
                <td className="text-bark-400 italic sticky left-0 bg-white pl-4">
                  <span className="flex items-center gap-1">
                    Unclassified Revenue
                    <span className="relative group not-italic">
                      <button className="text-bark-300 hover:text-forest-600 transition-colors cursor-pointer">&#9432;</button>
                      <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-64 p-2 text-xs text-bark-600 bg-white border border-grove-300 rounded-lg shadow-lg z-50 not-italic">
                        Difference between the document&apos;s stated total revenue and the sum of categorized items (Base Rent + Recovery + Other + Adjustments). Large values may indicate uncategorized revenue sources in the original document.
                      </span>
                    </span>
                  </span>
                </td>
                {years.map((y, i) => {
                  const categorized = (y.base_rent || 0) + (y.recovery_income || 0) + (y.other_income || 0) + (y.adjustments || 0);
                  const gap = y.total_revenue - categorized;
                  const pctOfTotal = y.total_revenue > 0 ? gap / y.total_revenue : 0;
                  return (
                    <td key={i} className={`num italic ${pctOfTotal > 0.1 ? "text-honey-700" : "text-bark-400"}`} title={pctOfTotal > 0.1 ? `${fmtPercent(pctOfTotal, 0)} of gross revenue is unclassified` : undefined}>
                      {gap > 100 ? fmtVal(gap) : "—"}{pctOfTotal > 0.1 && gap > 100 ? " ⚠" : ""}
                    </td>
                  );
                })}
              </tr>
            )}

            <tr className="border-t-2 border-grove-300 bg-forest-50/50">
              <td className="font-semibold text-forest-800">Gross Revenue</td>
              {years.map((y, i) => {
                // Always use parser's total_revenue when available — it comes
                // from the actual document. Fall back to sum of categories only
                // when total_revenue is missing or zero.
                const categorized = (y.base_rent || 0) + (y.recovery_income || 0) + (y.other_income || 0) + (y.adjustments || 0);
                const displayRevenue = y.total_revenue > 0 ? y.total_revenue : categorized;
                return (
                  <td key={i} className="num font-semibold text-forest-800">{fmtVal(displayRevenue)}</td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Expense Section */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <table className="w-full data-table text-[13px]">
          <thead>
            <tr>
              <th className="text-left min-w-[200px] sticky left-0 bg-white z-10">Operating Expenses</th>
              {years.map((y, i) => (
                <th key={i} className={`num min-w-[130px] ${isPartialYear(y) ? "text-bark-300" : ""}`}>
                  {y.year || y.period_label || `Period ${i + 1}`}
                  {isPartialYear(y) && (
                    <div className="text-[9px] text-honey-600 font-normal">
                      PARTIAL ({y.period_months ? `${y.period_months} mo` : 'YTD'})
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EXPENSE_GROUPS.map((group) => {
              const isExpanded = expandedGroups.has(group.key);

              return (
                <React.Fragment key={group.key}>
                  {/* Group subtotal row — always shown, even at $0 */}
                  <tr className="bg-grove-50/30">
                    <td className="font-semibold text-bark-700 sticky left-0 bg-grove-50/30">
                      <span className="flex items-center gap-1">
                        <button
                          onClick={() => toggleGroup(group.key)}
                          className="flex items-center gap-1 hover:text-forest-600 transition-colors"
                        >
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                          {group.label}
                        </button>
                        <DetailViewTrigger entityId={group.key} inputType="expense" onClick={openExpenseDetail} title="View expense detail" />
                        {overrideMode && (
                          <button
                            onClick={() => {
                              const existing = overrides.find(o => o.category === group.key);
                              setOverrideForm({
                                action: existing?.action || "replace",
                                value: existing?.value?.toString() || "",
                                note: existing?.note || "",
                              });
                              setEditingOverride(editingOverride === group.key ? null : group.key);
                            }}
                            className="ml-1 p-0.5 rounded text-honey-600 hover:text-honey-800 hover:bg-honey-100 transition-colors"
                            title="Adjust this expense"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {overrides.some(o => o.category === group.key) && (
                          <span className="ml-1 px-1.5 py-0.5 text-[9px] font-semibold bg-honey-100 text-honey-700 rounded-full uppercase">
                            {overrides.find(o => o.category === group.key)?.action === "exclude" ? "Excluded" : "Adjusted"}
                          </span>
                        )}
                      </span>
                    </td>
                    {years.map((y, i) => {
                      const sub = getGroupSubtotal(y, group);
                      return (
                        <td key={i} className="num font-semibold text-bark-700">
                          {fmtValZeroOk(sub)}
                          {showPSF && sub !== 0 && <div className="text-[9px] text-bark-400 font-normal">{psfAnnotation(sub, totalLeasableArea)}</div>}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Override editing row */}
                  {editingOverride === group.key && (
                    <tr className="bg-honey-50/60">
                      <td colSpan={years.length + 1} className="px-4 py-3">
                        <div className="flex items-end gap-3 flex-wrap">
                          <div>
                            <label className="text-[10px] font-semibold text-bark-500 uppercase tracking-wider block mb-1">Action</label>
                            <select
                              value={overrideForm.action}
                              onChange={(e) => setOverrideForm(f => ({ ...f, action: e.target.value }))}
                              className="px-2 py-1.5 text-xs border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-1 focus:ring-forest-500"
                            >
                              <option value="replace">Replace Amount</option>
                              <option value="exclude">Exclude Entirely</option>
                            </select>
                          </div>
                          {overrideForm.action === "replace" && (
                            <div>
                              <label className="text-[10px] font-semibold text-bark-500 uppercase tracking-wider block mb-1">New Amount</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-bark-400 text-xs">$</span>
                                <input
                                  type="text"
                                  value={overrideForm.value}
                                  onChange={(e) => setOverrideForm(f => ({ ...f, value: e.target.value.replace(/[^0-9.]/g, "") }))}
                                  className="pl-5 pr-2 py-1.5 w-32 text-xs border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-1 focus:ring-forest-500"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          )}
                          <div className="flex-1 min-w-[120px]">
                            <label className="text-[10px] font-semibold text-bark-500 uppercase tracking-wider block mb-1">Note</label>
                            <input
                              type="text"
                              value={overrideForm.note}
                              onChange={(e) => setOverrideForm(f => ({ ...f, note: e.target.value }))}
                              className="w-full px-2 py-1.5 text-xs border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-1 focus:ring-forest-500"
                              placeholder="Reason for adjustment"
                            />
                          </div>
                          <button
                            onClick={() => saveOverride(group.key)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-forest-700 text-white rounded-lg hover:bg-forest-800 transition-colors"
                          >
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button
                            onClick={() => setEditingOverride(null)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors"
                          >
                            <X className="w-3 h-3" /> Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Expanded child rows */}
                  {isExpanded && (() => {
                    // Use parsed expense_groups items if available
                    const hasGroups = years.some((y) => y.expense_groups?.[group.key]?.items?.length);
                    if (hasGroups) {
                      const nameSet = new Set<string>();
                      years.forEach((y) => {
                        y.expense_groups?.[group.key]?.items?.forEach((it) => nameSet.add(it.name));
                      });
                      return Array.from(nameSet).map((name) => (
                        <tr key={name} className="bg-grove-50/50">
                          <td className="pl-10 pr-5 py-2 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
                            <span className="flex items-center gap-1">
                              {name}
                              {overrideMode && (
                                <button
                                  onClick={() => handleDeleteLineItem(group.key, name)}
                                  className="ml-auto p-0.5 rounded text-bark-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Remove line item"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          </td>
                          {years.map((y, i) => {
                            const item = y.expense_groups?.[group.key]?.items?.find((it) => it.name === name);
                            const cellKey = `${group.key}:${name}:${i}`;
                            const isEditing = editingCell === cellKey;
                            return (
                              <td
                                key={i}
                                className={`px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400 ${overrideMode && !isEditing ? "cursor-pointer hover:bg-honey-50" : ""}`}
                                onClick={overrideMode && !isEditing ? () => { setEditingCell(cellKey); setEditingCellValue(String(item?.amount ?? 0)); } : undefined}
                              >
                                {isEditing ? (
                                  <input
                                    type="number"
                                    autoFocus
                                    value={editingCellValue}
                                    onChange={(e) => setEditingCellValue(e.target.value)}
                                    onBlur={() => commitCellEdit(group.key, name)}
                                    onKeyDown={(e) => { if (e.key === "Enter") commitCellEdit(group.key, name); if (e.key === "Escape") setEditingCell(null); }}
                                    className="w-full px-1 py-0.5 text-[11px] text-right tabular-nums bg-white border border-forest-500 rounded focus:outline-none focus:ring-1 focus:ring-forest-500"
                                  />
                                ) : fmtVal(item?.amount ?? 0)}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    }
                    // Fallback: use flat fields
                    return group.children.map((child) => (
                      <tr key={child.key} className="bg-grove-50/50">
                        <td className="pl-10 pr-5 py-2 border-b border-grove-100 sticky left-0 bg-grove-50/50 text-[11px] text-bark-400">
                          {child.label}
                        </td>
                        {years.map((y, i) => {
                          const cellKey = `${group.key}:${child.label}:${i}`;
                          const isEditing = editingCell === cellKey;
                          const val = y[child.key as keyof YearData] as number;
                          return (
                            <td
                              key={i}
                              className={`px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400 ${overrideMode && !isEditing ? "cursor-pointer hover:bg-honey-50" : ""}`}
                              onClick={overrideMode && !isEditing ? () => { setEditingCell(cellKey); setEditingCellValue(String(val ?? 0)); } : undefined}
                            >
                              {isEditing ? (
                                <input
                                  type="number"
                                  autoFocus
                                  value={editingCellValue}
                                  onChange={(e) => setEditingCellValue(e.target.value)}
                                  onBlur={() => commitCellEdit(group.key, child.label)}
                                  onKeyDown={(e) => { if (e.key === "Enter") commitCellEdit(group.key, child.label); if (e.key === "Escape") setEditingCell(null); }}
                                  className="w-full px-1 py-0.5 text-[11px] text-right tabular-nums bg-white border border-forest-500 rounded focus:outline-none focus:ring-1 focus:ring-forest-500"
                                />
                              ) : fmtVal(val)}
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                </React.Fragment>
              );
            })}
            {/* Below-the-line items (capex, reserves) */}
            {BELOW_LINE_CATEGORIES.map((cat) => {
              const hasValues = years.some((y) => (y[cat.key as keyof YearData] as number) !== 0);
              if (!hasValues) return null;
              return (
                <tr key={cat.key}>
                  <td className="text-bark-600">
                    <span className="flex items-center gap-1">
                      {cat.label}
                      <DetailViewTrigger entityId={cat.key} inputType="capital" onClick={openCapitalDetail} title="View expense detail" />
                    </span>
                  </td>
                  {years.map((y, i) => (
                    <td key={i} className="num">
                      {fmtVal(y[cat.key as keyof YearData] as number)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* Total Operating Expenses — uses parser's total_expenses (includes uncategorized items) */}
            <tr className="border-t-2 border-grove-300 bg-grove-100/50">
              <td className="font-semibold text-bark-800">Total Operating Expenses</td>
              {years.map((y, i) => {
                const total = y.total_expenses ?? getComputedTotalExpenses(y);
                return (
                  <td key={i} className="num font-semibold text-bark-800">
                    {fmtVal(total)}
                    {showPSF && total !== 0 && <div className="text-[9px] text-bark-400 font-normal">{psfAnnotation(total, totalLeasableArea)}</div>}
                  </td>
                );
              })}
            </tr>
            {/* Non-Operating Expenses */}
            {(() => {
              const hasNonOp = years.some((y) =>
                y.expense_items?.some((it) => it.is_non_operating && !it.is_subtotal && it.amount !== 0)
              );
              if (!hasNonOp) return null;
              const labels = new Set<string>();
              years.forEach((y) =>
                y.expense_items?.forEach((it) => {
                  if (it.is_non_operating && !it.is_subtotal && it.amount !== 0) labels.add(it.label);
                })
              );
              return (
                <>
                  <tr>
                    <td colSpan={years.length + 1} className="h-3" />
                  </tr>
                  <tr className="bg-bark-50/30">
                    <td className="text-[10px] font-semibold uppercase tracking-[0.06em] text-bark-400 sticky left-0 bg-bark-50/30" title="These items are excluded from NOI because they are owner-specific financing or accounting entries, not costs of operating the property.">
                      Non-Operating (excluded) <span className="text-bark-300 cursor-help">&#9432;</span>
                    </td>
                    {years.map((_, i) => (
                      <td key={i} />
                    ))}
                  </tr>
                  {Array.from(labels).map((lbl) => (
                    <tr key={lbl} className="bg-bark-50/20">
                      <td className="text-bark-400 text-[11px] italic pl-5 sticky left-0 bg-bark-50/20">{lbl}</td>
                      {years.map((y, i) => {
                        const item = y.expense_items?.find((it) => it.is_non_operating && it.label === lbl);
                        return (
                          <td key={i} className="num text-bark-400 text-[11px] italic">
                            {fmtVal(item?.amount ?? 0)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* NOI Section — uses parser's noi value (ties to source document) */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <table className="w-full data-table text-[13px]">
          <thead>
            <tr>
              <th className="text-left min-w-[200px] sticky left-0 bg-white z-10">Net Operating Income</th>
              {years.map((y, i) => (
                <th key={i} className={`num min-w-[130px] ${isPartialYear(y) ? "text-bark-300" : ""}`}>
                  {y.year || y.period_label || `Period ${i + 1}`}
                  {isPartialYear(y) && (
                    <div className="text-[9px] text-honey-600 font-normal">
                      PARTIAL ({y.period_months ? `${y.period_months} mo` : 'YTD'})
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-honey-50">
              <td className="font-bold text-honey-800">NOI</td>
              {years.map((y, i) => {
                const partial = isPartialYear(y);
                const noi = y.noi != null ? Math.round(y.noi) : Math.round((y.total_revenue || 0) - (y.total_expenses ?? getComputedTotalExpenses(y)));
                return (
                  <td key={i} className={`num font-bold ${partial ? "text-bark-300 line-through" : "text-honey-800"}`} title={partial ? "Partial year — $0 revenue, expenses only" : undefined}>
                    {fmtVal(noi)}
                    {showPSF && noi !== 0 && !partial && <div className="text-[9px] text-bark-400 font-normal">{psfAnnotation(noi, totalLeasableArea)}</div>}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Flags */}
      {opexData.flags && opexData.flags.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Parse Notes ({opexData.flags.length})
          </h3>
          <div className="space-y-2">
            {opexData.flags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2 px-4 py-3 rounded-lg text-xs border bg-honey-50 text-honey-800 border-honey-200">
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expense Detail Panel */}
      <DetailViewPanel
        isOpen={expDetailOpen}
        onClose={() => setExpDetailOpen(false)}
        entity={{ id: expDetailCategory, name: expDetailCategoryName, subtitle: "Operating Expense Detail" }}
        onPrev={null}
        onNext={null}
        tabs={[
          {
            label: "Expense Detail",
            content: (
              <ExpenseDetailPanel
                category={expDetailCategoryName}
                currentAmount={
                  years.length > 0
                    ? (() => {
                        const lastYear = years[years.length - 1];
                        const group = EXPENSE_GROUPS.find(g => g.key === expDetailCategory);
                        return group ? getGroupSubtotal(lastYear, group) : ((lastYear[expDetailCategory as keyof YearData] as number) || 0);
                      })()
                    : 0
                }
                growthRate={0.03}
                lineItems={expDetailLineItems}
                entries={expDetailEntries}
                onEntryChange={handleExpEntryChange}
                onEntryAdd={handleExpEntryAdd}
                onEntryDelete={handleExpEntryDelete}
                onSave={saveExpDetail}
                onReset={resetExpDetail}
                isDirty={expDetailDirty}
                isSaving={expDetailSaving}
                projectedSchedule={expDetailSchedule}
              />
            ),
          },
        ]}
        activeTab={0}
        onTabChange={() => {}}
        hasUnsavedChanges={expDetailDirty}
      />

      {/* Capital Detail Panel */}
      <DetailViewPanel
        isOpen={capDetailOpen}
        onClose={() => setCapDetailOpen(false)}
        entity={{ id: "capital_expenditures", name: "Capital Expenditures", subtitle: "Below-the-line capital items" }}
        onPrev={null}
        onNext={null}
        tabs={[
          {
            label: "Capital Detail",
            content: (
              <CapitalDetailPanel
                entries={capDetailEntries}
                onEntryChange={handleCapEntryChange}
                onEntryAdd={handleCapEntryAdd}
                onEntryDelete={handleCapEntryDelete}
                onSave={saveCapDetail}
                onReset={resetCapDetail}
                isDirty={capDetailDirty}
                isSaving={capDetailSaving}
              />
            ),
          },
        ]}
        activeTab={0}
        onTabChange={() => {}}
        hasUnsavedChanges={capDetailDirty}
      />
      </>}
    </div>
  );
}

function psfAnnotation(val: number | undefined | null, area: number | undefined): string {
  if (!val || !area || area <= 0) return "";
  return fmtPSF(val / area);
}

function fmtVal(val: number | undefined | null): string {
  if (val == null || val === 0) return "\u2014";
  // Accounting style: parens negatives + trailing figure-space so mixed
  // positive/negative columns (e.g. adjustments, NOI) stay digit-aligned.
  return fmtCurrencyAcct(val);
}

/** Like fmtVal but shows $0 instead of em-dash for zero values. */
function fmtValZeroOk(val: number | undefined | null): string {
  if (val == null) return "\u2014";
  return fmtCurrencyAcct(val);
}
